import React, { useState, useEffect } from 'react';
import ApiManager from '../services/api/ApiManager';
import { MapPin, Navigation, Truck, Layers } from 'lucide-react';
import { useUser } from '../contexts/UserContext';
import { useTheme } from '../contexts/ThemeContext';
import { useActivityLogger } from '../hooks/useActivityLogger';
import BubbleMapResults from './BubbleMapResults';
import RouteMapVisualization from './RouteMapVisualization';
import CityAutocomplete, { getMapboxCitySuggestions } from './CityAutocomplete';

const RouteOptimizationUnified = () => {
  // User context for rate limiting
  const { userRole } = useUser();
  const { isDarkMode } = useTheme();
  const { logRouteOptimization } = useActivityLogger();
  
  const [formData, setFormData] = useState({
    origin: 'Chicago, IL',
    equipment_type: 'Flatbed',
    radius: 150,
    max_trips: 3,
    cost_per_mile: 1.85,
    min_mileage: 100
  });
  
  const [routeData, setRouteData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [mapBackground, setMapBackground] = useState(() => isDarkMode ? 'dark' : 'light');

  // Update map background when theme changes
  useEffect(() => {
    setMapBackground(isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  // Rate limiting state for Route Optimization
  const [routeSearchCount, setRouteSearchCount] = useState(() => {
    try {
      const today = new Date().toDateString();
      const savedData = localStorage.getItem('lotwizard_route_optimization_usage');
      if (savedData) {
        const data = JSON.parse(savedData);
        // Reset count if it's a new day
        if (data.date === today) {
          return {
            daily: data.daily || 0,
            weekly: data.weekly || 0,
            weekStart: data.weekStart || today
          };
        }
      }
      return { daily: 0, weekly: 0, weekStart: today };
    } catch (error) {
      console.warn('Error loading route optimization usage from localStorage:', error);
      return { daily: 0, weekly: 0, weekStart: new Date().toDateString() };
    }
  });

  // Equipment type mappings
  const equipmentTypes = [
    { value: 'Flatbed', label: 'Flatbed' },
    { value: 'Dry Van', label: 'Dry Van' },
    { value: 'Reefer', label: 'Reefer' }
  ];

  // Rate limiting functions for Route Optimization
  const checkRouteOptimizationLimit = () => {
    // No limits for Premium, Admin, or Dev users
    if (['Premium', 'Admin', 'Dev'].includes(userRole)) return { allowed: true };
    
    const DAILY_LIMIT = 3;
    const WEEKLY_LIMIT = 10;
    
    // Check if it's a new week (reset weekly counter)
    const today = new Date();
    const weekStart = new Date(routeSearchCount.weekStart);
    const daysDiff = Math.floor((today - weekStart) / (1000 * 60 * 60 * 24));
    
    let currentWeekly = routeSearchCount.weekly;
    if (daysDiff >= 7) {
      // Reset weekly counter if it's been a week
      currentWeekly = 0;
    }
    
    if (routeSearchCount.daily >= DAILY_LIMIT) {
      return { 
        allowed: false, 
        reason: 'daily',
        remaining: { daily: 0, weekly: Math.max(0, WEEKLY_LIMIT - currentWeekly) }
      };
    }
    
    if (currentWeekly >= WEEKLY_LIMIT) {
      return { 
        allowed: false, 
        reason: 'weekly',
        remaining: { daily: Math.max(0, DAILY_LIMIT - routeSearchCount.daily), weekly: 0 }
      };
    }
    
    return { 
      allowed: true,
      remaining: { 
        daily: Math.max(0, DAILY_LIMIT - routeSearchCount.daily), 
        weekly: Math.max(0, WEEKLY_LIMIT - currentWeekly) 
      }
    };
  };

  const incrementRouteOptimizationCount = () => {
    // Only track for non-Premium users
    if (['Premium', 'Admin', 'Dev'].includes(userRole)) return;
    
    const today = new Date();
    const todayString = today.toDateString();
    const weekStart = new Date(routeSearchCount.weekStart);
    const daysDiff = Math.floor((today - weekStart) / (1000 * 60 * 60 * 24));
    
    // Reset weekly counter if it's been a week
    const newWeekly = daysDiff >= 7 ? 1 : routeSearchCount.weekly + 1;
    const newWeekStart = daysDiff >= 7 ? todayString : routeSearchCount.weekStart;
    
    const newCount = {
      daily: routeSearchCount.daily + 1,
      weekly: newWeekly,
      weekStart: newWeekStart
    };
    
    try {
      localStorage.setItem('lotwizard_route_optimization_usage', JSON.stringify({
        date: todayString,
        ...newCount
      }));
      setRouteSearchCount(newCount);
      console.log('[RouteOptimization] Usage updated:', newCount);
    } catch (error) {
      console.warn('Error saving route optimization usage to localStorage:', error);
    }
  };

  const getRemainingOptimizations = () => {
    if (['Premium', 'Admin', 'Dev'].includes(userRole)) return null; // Unlimited
    
    const limitCheck = checkRouteOptimizationLimit();
    return limitCheck.remaining;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check rate limit for non-Premium users
    const limitCheck = checkRouteOptimizationLimit();
    if (!limitCheck.allowed) {
      if (limitCheck.reason === 'daily') {
        setError(`Daily limit reached. You can optimize ${limitCheck.remaining.daily} more routes today and ${limitCheck.remaining.weekly} more this week. Upgrade to Premium for unlimited route optimizations.`);
      } else if (limitCheck.reason === 'weekly') {
        setError(`Weekly limit reached. You can optimize ${limitCheck.remaining.daily} more routes today but have reached your weekly limit of 10. Upgrade to Premium for unlimited route optimizations.`);
      }
      return;
    }
    
    setLoading(true);
    setError(null);

    // Verify the typed origin actually resolves to the city/state shown before
    // sending it to the backend. Prevents silently submitting an ambiguous or
    // mistyped city (e.g. "Cleveland, OH" resolving to Cleveland, TX) with no
    // indication anything went wrong - same failure mode fixed in Lane Pricing.
    try {
      const suggestions = await getMapboxCitySuggestions(formData.origin);
      const normalizedOrigin = formData.origin.trim().toLowerCase();
      const isVerified = suggestions.some(
        (s) => s.trim().toLowerCase() === normalizedOrigin
      );
      if (!isVerified) {
        setError(
          `Couldn't verify "${formData.origin}" as an exact city match. Please pick your city from the suggestions dropdown to make sure the right one is used.`
        );
        setLoading(false);
        return;
      }
    } catch (validationError) {
      console.warn('Origin validation check failed, proceeding without it:', validationError);
    }

    try {
      const response = await ApiManager.getRouteOptimization(formData);
      setRouteData(response.data);

      // Log route optimization activity using centralized logger
      try {
        await logRouteOptimization({
          origin: formData.origin,
          equipment_type: formData.equipment_type,
          radius: formData.radius,
          max_trips: formData.max_trips,
          cost_per_mile: formData.cost_per_mile,
          min_mileage: formData.min_mileage
        });
      } catch (logError) {
        console.warn('Failed to log route optimization activity:', logError);
      }

      // Increment usage count for successful optimization
      incrementRouteOptimizationCount();
    } catch (err) {
      console.error('Route optimization error:', err);
      setError(err.message || 'Failed to optimize route');
      
      // Provide mock data for demonstration
      const mockData = {
        route_plan: [
          {
            trip_number: 1,
            origin: "Chicago, IL",
            destination: "Indianapolis, IN",
            distance: 185,
            payment: 850,
            profit: 507,
            driving_time: 3.2,
            outbound_opportunities: 12
          },
          {
            trip_number: 2,
            origin: "Indianapolis, IN", 
            destination: "Nashville, TN",
            distance: 288,
            payment: 1150,
            profit: 617,
            driving_time: 4.8,
            outbound_opportunities: 8
          }
        ],
        summary: {
          total_trips: 2,
          total_distance: 473,
          total_payment: 2000,
          total_profit: 1124,
          total_driving_time: 8.0,
          avg_profit_per_hour: 140.5,
          avg_rate_per_mile: 4.23
        },
        alternatives: [
          { destination: "Louisville, KY", profit_potential: 580, opportunities: 15 },
          { destination: "Milwaukee, WI", profit_potential: 445, opportunities: 9 }
        ]
      };
      setRouteData(mockData);
      
      // Increment usage count for mock data too (still uses the feature)
      incrementRouteOptimizationCount();
    } finally {
      setLoading(false);
    }
  };

  // Helper functions for map data processing (similar to Search Loads)
  const aggregateRouteDataForMap = () => {
    if (!routeData?.route_plan) return [];
    
    // Group routes by destination city for bubble map display
    const cityGroups = {};
    
    routeData.route_plan.forEach(trip => {
      const key = trip.destination;
      if (!cityGroups[key]) {
        cityGroups[key] = {
          cityName: trip.destination,
          loads: []
        };
      }
      
      // Convert trip to load format for map display
      cityGroups[key].loads.push({
        ID: `route-${trip.trip_number}`,
        PaymentAmount: trip.payment_amount || trip.payment || 0,
        RatePerMile: trip.rate_per_mile || 0,
        Mileage: trip.mileage || trip.distance || 0,
        Equipment: formData.equipment_type?.charAt(0) || 'F',
        OriginCity: trip.origin.split(', ')[0],
        OriginState: trip.origin.split(', ')[1],
        DestinationCity: trip.destination.split(', ')[0],
        DestinationState: trip.destination.split(', ')[1]
      });
    });
    
    // Convert to array format for bubble map
    return Object.values(cityGroups).map(group => ({
      cityName: group.cityName,
      loads: group.loads,
      loadCount: group.loads.length,
      totalPayment: group.loads.reduce((sum, load) => sum + (load.PaymentAmount || 0), 0),
      avgPayment: group.loads.reduce((sum, load) => sum + (load.PaymentAmount || 0), 0) / group.loads.length,
      avgRate: group.loads.reduce((sum, load) => sum + (load.RatePerMile || 0), 0) / group.loads.length
    }));
  };

  const formatPaymentForBubble = (amount) => {
    if (!amount) return '$0';
    if (amount >= 1000000) return `$${(amount / 1000000).toFixed(1)}M`;
    if (amount >= 1000) return `$${(amount / 1000).toFixed(1)}k`;
    return `$${Math.round(amount)}`;
  };

  const getPaymentColor = (amount) => {
    if (!amount) return '#94a3b8'; // gray-400
    if (amount >= 3000) return '#059669'; // emerald-600
    if (amount >= 2000) return '#0d9488'; // teal-600
    if (amount >= 1000) return '#0891b2'; // cyan-600
    return '#3b82f6'; // blue-600
  };

  const formatNumber = (num) => {
    return num?.toLocaleString() || '0';
  };

  const formatCurrency = (amount) => {
    return `$${formatNumber(amount)}`;
  };

  const handleCityClick = (cityData) => {
    console.log('Route destination clicked:', cityData);
  };

  const handleSetOrigin = (cityName) => {
    setFormData(prev => ({
      ...prev,
      origin: cityName
    }));
  };

  const renderInteractiveMap = () => {
    if (!routeData?.route_plan?.length) return null;
    
    const mapData = aggregateRouteDataForMap();
    
    return (
      <div className="mt-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden">
          <div className="p-4 border-b border-gray-200 dark:border-gray-600">
            <h4 className="font-semibold text-gray-900 dark:text-white flex items-center">
              <Navigation className="w-5 h-5 mr-2" />
              Route Map
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {routeData.route_plan.length} trips to {mapData.length} destinations
            </p>
          </div>
          <div className="rounded-b-lg overflow-hidden">
            <RouteMapVisualization 
              routeData={routeData}
              origin={formData.origin}
              isDarkMode={isDarkMode}
              mapBackground={mapBackground}
            />
          </div>
        </div>
        
        {/* Map Controls */}
        <div className="mt-4 flex justify-center">
          <div className="flex items-center space-x-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg px-4 py-2 border border-gray-200 dark:border-gray-700">
            <Layers className="w-4 h-4 text-gray-600 dark:text-gray-400" />
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Background:
            </label>
            <select
              value={mapBackground}
              onChange={(e) => setMapBackground(e.target.value)}
              className="text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
            >
              <option value="light">Light</option>
              <option value="satellite">Satellite</option>
              <option value="dark">Dark</option>
              <option value="streets">Streets</option>
            </select>
          </div>
        </div>
      </div>
    );
  };

  const renderRouteMap = () => {
    if (!routeData?.route_plan) return null;
    
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden">
        <div className="p-4 border-b border-gray-200 dark:border-gray-600">
          <h4 className="font-semibold text-gray-900 dark:text-white flex items-center">
            <Navigation className="w-5 h-5 mr-2" />
            Route Visualization
          </h4>
          <p className="text-sm text-gray-600 dark:text-gray-400">{routeData.route_plan.length} trips planned</p>
        </div>
        <div className="p-4">
          <div className="space-y-4">
            {routeData.route_plan.map((trip, index) => (
              <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                    {trip.trip_number}
                  </div>
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-2 text-sm">
                    <MapPin className="w-4 h-4 text-green-600" />
                    <span className="font-medium text-gray-900 dark:text-white">{trip.origin}</span>
                    <span className="text-gray-500">→</span>
                    <MapPin className="w-4 h-4 text-red-600" />
                    <span className="font-medium text-gray-900 dark:text-white">{trip.destination}</span>
                  </div>
                  <div className="flex items-center space-x-4 mt-1 text-xs text-gray-600 dark:text-gray-400">
                    <span className="flex items-center">
                      <Truck className="w-3 h-3 mr-1" />
                      {trip.mileage} mi
                    </span>
                    <span className="text-green-600 dark:text-green-400 font-semibold">
                      ${Math.round(trip.profit)} profit
                    </span>
                    <span>${trip.rate_per_mile}/mi</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Route Summary */}
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded">
                <div className="text-blue-600 dark:text-blue-400 font-semibold">
                  {routeData.route_plan.reduce((sum, trip) => sum + (trip.mileage || 0), 0)}
                </div>
                <div className="text-gray-600 dark:text-gray-400">Total Miles</div>
              </div>
              <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded">
                <div className="text-green-600 dark:text-green-400 font-semibold">
                  ${Math.round(routeData.route_plan.reduce((sum, trip) => sum + (trip.profit || 0), 0))}
                </div>
                <div className="text-gray-600 dark:text-gray-400">Total Profit</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderSummaryMetrics = () => {
    if (!routeData?.summary) return null;
    
    const { summary } = routeData;
    
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg border border-green-200 dark:border-green-800">
          <p className="text-sm text-gray-600 dark:text-gray-400">Total Profit</p>
          <p className="text-xl font-bold text-green-600 dark:text-green-400">
            ${Math.round(summary.total_profit || 0).toLocaleString()}
          </p>
        </div>
        <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border border-blue-200 dark:border-blue-800">
          <p className="text-sm text-gray-600 dark:text-gray-400">Total Miles</p>
          <p className="text-xl font-bold text-blue-600 dark:text-blue-400">
            {(summary.total_miles || 0).toLocaleString()} mi
          </p>
        </div>
        <div className="bg-purple-50 dark:bg-purple-900/20 p-3 rounded-lg border border-purple-200 dark:border-purple-800">
          <p className="text-sm text-gray-600 dark:text-gray-400">Avg Revenue/Mile</p>
          <p className="text-xl font-bold text-purple-600 dark:text-purple-400">
            ${(summary.avg_revenue_per_mile || 0).toFixed(2)}
          </p>
        </div>
        <div className="bg-orange-50 dark:bg-orange-900/20 p-3 rounded-lg border border-orange-200 dark:border-orange-800">
          <p className="text-sm text-gray-600 dark:text-gray-400">Total Trips</p>
          <p className="text-xl font-bold text-orange-600 dark:text-orange-400">
            {summary.total_trips || 0}
          </p>
        </div>
      </div>
    );
  };

  const renderRouteDetails = () => {
    if (!routeData?.route_plan) return null;
    
    return (
      <div className="space-y-4">
        <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Trip Details</h4>
        {routeData.route_plan.map((trip, index) => (
          <div key={index} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 bg-white dark:bg-gray-800">
            <div className="flex justify-between items-start mb-2">
              <h5 className="font-semibold text-gray-900 dark:text-white">Trip {trip.trip_number}</h5>
              <span className="text-sm px-2 py-1 bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-200 rounded">
                ${Math.round(trip.profit)} profit
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
              <div>
                <span className="text-gray-600 dark:text-gray-400">Route:</span>
                <p className="text-gray-900 dark:text-white">{trip.origin} → {trip.destination}</p>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">Payment:</span>
                <p className="text-gray-900 dark:text-white">${trip.payment_amount?.toLocaleString()}</p>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">Distance:</span>
                <p className="text-gray-900 dark:text-white">{trip.mileage} miles</p>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">Rate/Mile:</span>
                <p className="text-gray-900 dark:text-white">${trip.rate_per_mile}</p>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">Outbound Loads:</span>
                <p className="text-gray-900 dark:text-white">{trip.outbound_opportunities} available</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderAlternatives = () => {
    if (!routeData?.alternatives?.length) return null;
    
    return (
      <div className="mt-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">Alternative Destinations</h4>
        <div className="grid gap-2">
          {routeData.alternatives.map((alt, index) => (
            <div key={index} className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600">
              <span className="font-medium text-gray-900 dark:text-white">{alt.destination}</span>
              <div className="text-right text-sm">
                <div className="text-green-600 dark:text-green-400 font-semibold">${alt.profit_potential} potential</div>
                <div className="text-gray-500 dark:text-gray-400">{alt.opportunities} opportunities</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Route Optimization</h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          Plan optimized multi-trip routes with profit analysis and opportunity scoring
        </p>
      </div>

      {/* Rate Limiting Notice for Basic/Standard users */}
      {!['Premium', 'Admin', 'Dev'].includes(userRole) && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <div className="w-5 h-5 bg-blue-100 dark:bg-blue-800 rounded-full flex items-center justify-center">
              <span className="text-blue-600 dark:text-blue-400 text-xs">ℹ</span>
            </div>
            <div>
              <p className="text-blue-700 dark:text-blue-300 text-sm font-medium">Route Optimization Limits</p>
              <p className="text-blue-600 dark:text-blue-400 text-sm">
                {(() => {
                  const remaining = getRemainingOptimizations();
                  return remaining ? `You have ${remaining.daily} optimizations remaining today and ${remaining.weekly} remaining this week. Upgrade to Premium for unlimited route optimizations.` : 'Loading usage information...';
                })()}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Input Form */}
      <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
          <div className="md:col-span-2">
            <CityAutocomplete
              label="Origin City, State"
              id="origin"
              name="origin"
              value={formData.origin}
              onChange={(e) => handleInputChange('origin', e.target.value)}
              placeholder="Chicago, IL"
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
              Equipment Type
            </label>
            <select
              value={formData.equipment_type}
              onChange={(e) => handleInputChange('equipment_type', e.target.value)}
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {equipmentTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
              Max Radius (miles)
            </label>
            <input
              type="range"
              min="25"
              max="300"
              step="25"
              value={formData.radius}
              onChange={(e) => handleInputChange('radius', parseInt(e.target.value))}
              className="w-full"
            />
            <div className="text-center text-sm text-gray-600 dark:text-gray-400">{formData.radius}</div>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
              Max Trips
            </label>
            <input
              type="number"
              min="1"
              max="10"
              value={formData.max_trips}
              onChange={(e) => handleInputChange('max_trips', parseInt(e.target.value))}
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
              Cost Per Mile ($)
            </label>
            <input
              type="number"
              step="0.01"
              value={formData.cost_per_mile}
              onChange={(e) => handleInputChange('cost_per_mile', parseFloat(e.target.value))}
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
              Min Mileage Per Trip
            </label>
            <input
              type="range"
              min="50"
              max="500"
              step="25"
              value={formData.min_mileage}
              onChange={(e) => handleInputChange('min_mileage', parseInt(e.target.value))}
              className="w-full"
            />
            <div className="text-center text-sm text-gray-600 dark:text-gray-400">{formData.min_mileage} miles</div>
          </div>
        </div>
        
        <button
          type="submit"
          disabled={loading}
          className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-6 py-2 rounded transition-colors"
        >
          {loading ? 'Optimizing Route...' : 'Optimize Route'}
        </button>
      </form>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-4 mb-6">
          <p className="text-red-800 dark:text-red-400">Error: {error}</p>
          <p className="text-red-600 dark:text-red-400 text-sm mt-1">Showing mock data for demonstration</p>
        </div>
      )}

      {/* Results */}
      {routeData && (
        <div className="space-y-6">
          {/* Summary Metrics */}
          {renderSummaryMetrics()}
          
          {/* No Routes Found Message */}
          {routeData.route_plan && routeData.route_plan.length === 0 && (
            <div className="text-center py-12 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
              <div className="text-yellow-600 dark:text-yellow-400">
                <h3 className="text-lg font-semibold mb-2">No Routes Found</h3>
                <p className="text-sm mb-4">
                  No suitable loads found within {formData.radius} miles of {formData.origin}.
                </p>
                <p className="text-xs text-yellow-500 dark:text-yellow-400">
                  Try increasing the radius or selecting a different origin city with more freight activity.
                </p>
              </div>
            </div>
          )}
          
          {/* Route Map and Details */}
          {routeData.route_plan && routeData.route_plan.length > 0 && (
            <div className="space-y-6">
              {/* Interactive Map */}
              {renderInteractiveMap()}
              
              {/* Route Details Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-1">
                  {renderRouteMap()}
                </div>
                <div className="lg:col-span-2">
                  {renderRouteDetails()}
                </div>
              </div>
            </div>
          )}
          
          {/* Alternative Destinations */}
          {renderAlternatives()}
        </div>
      )}
    </div>
  );
};

export default RouteOptimizationUnified;